/**
 * Client wrappers for the parent <-> child connection Cloud Functions.
 *
 * Flow:
 *   - The child calls createConnectionCode() and shares the returned code
 *     (shown as text + QR) with a parent.
 *   - The child polls getConnectionStatus() until status === "linked".
 *   - The parent calls linkParentToChild() with the scanned/typed code.
 *
 * See serverless/functions/src/index.ts for the matching server logic and
 * utils/parentStudentFirestore.ts for reading the resulting links.
 */
import { callCloudFunction, FIREBASE_FUNCTIONS } from './firebaseFunctions';
import { DEEP_LINK_BASE_URL } from './common';

const JSON_HEADERS = { 'Content-Type': 'application/json' };

export interface CreateCodeResult {
  success: boolean;
  code?: string;
  expiresAt?: string;
  error?: string;
}

export interface ConnectionStatusResult {
  success: boolean;
  status?: 'pending' | 'linked';
  childName?: string;
  childEmail?: string;
  parentEmail?: string | null;
  parentName?: string | null;
  expired?: boolean;
  error?: string;
}

export interface LinkResult {
  success: boolean;
  childName?: string;
  childEmail?: string;
  error?: string;
}

export interface UnlinkResult {
  success: boolean;
  error?: string;
}

/** The child requests (or re-uses) a connection code to share with a parent. */
export async function createConnectionCode(params: {
  childEmail: string;
  childName: string;
  userId?: string | null;
}): Promise<CreateCodeResult> {
  try {
    const res = await callCloudFunction(
      'createConnectionCode',
      FIREBASE_FUNCTIONS.createConnectionCode(),
      {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({
          childEmail: params.childEmail,
          childName: params.childName,
          userId: params.userId ?? null,
        }),
      },
    );
    return (await res.json()) as CreateCodeResult;
  } catch (err) {
    console.warn('createConnectionCode failed:', err);
    return { success: false, error: 'network' };
  }
}

/** The child polls the status of a code to detect when a parent has linked. */
export async function getConnectionStatus(code: string): Promise<ConnectionStatusResult> {
  try {
    const res = await callCloudFunction(
      'getConnectionStatus',
      FIREBASE_FUNCTIONS.getConnectionStatus(),
      {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ code }),
      },
    );
    return (await res.json()) as ConnectionStatusResult;
  } catch (err) {
    console.warn('getConnectionStatus failed:', err);
    return { success: false, error: 'network' };
  }
}

/** The parent submits a scanned/typed code to link to a child. */
export async function linkParentToChild(params: {
  code: string;
  parentEmail: string;
  parentName?: string;
}): Promise<LinkResult> {
  try {
    const res = await callCloudFunction(
      'linkParentToChild',
      FIREBASE_FUNCTIONS.linkParentToChild(),
      {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({
          code: params.code,
          parentEmail: params.parentEmail,
          parentName: params.parentName ?? '',
        }),
      },
    );
    return (await res.json()) as LinkResult;
  } catch (err) {
    console.warn('linkParentToChild failed:', err);
    return { success: false, error: 'Server error. Please try again.' };
  }
}

/** Removes a parent <-> child connection. Either side may call this. */
export async function unlinkConnection(params: {
  parentEmail: string;
  childEmail: string;
}): Promise<UnlinkResult> {
  try {
    const res = await callCloudFunction(
      'unlinkParentChild',
      FIREBASE_FUNCTIONS.unlinkParentChild(),
      {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({
          parentEmail: params.parentEmail,
          childEmail: params.childEmail,
        }),
      },
    );
    return (await res.json()) as UnlinkResult;
  } catch (err) {
    console.warn('unlinkConnection failed:', err);
    return { success: false, error: 'network' };
  }
}

/**
 * Normalizes a scanned QR payload down to a bare connection code.
 * Accepts either a raw code ("K7P2QX") or a deep link
 * (".../?connectionId=K7P2QX", legacy ".../connect-parent?code=K7P2QX")
 * and returns the uppercased code, or null.
 */
export function extractCodeFromScan(raw: string): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  // Try to pull a ?connectionId= / ?code= param out of a URL.
  const match = trimmed.match(/[?&](?:connectionId|code)=([A-Za-z0-9]+)/);
  const candidate = match ? match[1] : trimmed;
  const cleaned = candidate.toUpperCase().replace(/[^A-Z0-9]/g, '');
  return cleaned.length >= 4 ? cleaned : null;
}

/**
 * Builds the QR image URL for a connection code. The QR encodes the same
 * `?connectionId=` deep link used for the WhatsApp/copy-link share text
 * (see DEEP_LINK_BASE_URL) — NOT a path appended to the TinyURL alias, since
 * TinyURL vanity aliases only redirect their exact slug and 404 on anything
 * appended after it.
 */
export function connectionQrImageUrl(code: string, size = 220): string {
  const data = `${DEEP_LINK_BASE_URL}?connectionId=${encodeURIComponent(code)}`;
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&margin=8&data=${encodeURIComponent(
    data,
  )}`;
}
